import { inject, Injectable } from '@angular/core';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  fromEvent,
  interval,
  map,
  Observable,
  shareReplay,
  startWith,
} from 'rxjs';
import { Vault } from './vault';
export type RedeemState = {
  codeId: string;

  // merchant ที่ผูกกับหน้าต่างนี้ (route param key เช่น 'JUSTJUICE')
  merchantName: string;

  // เวลาเมื่อเริ่มหน้าต่าง (ms epoch)
  issuedAt: number;
  // เวลาเมื่อหมดอายุ (ms epoch)
  expiresAt: number;
  // สำหรับตรวจจับ clock-skew แบบหยาบ ๆ
  startedAtPerf: number;
};

const STORAGE_KEY = 'redeem_window_state';
const DEFAULT_WINDOW_MS = 30 * 60 * 1000; // 30 นาที

@Injectable({ providedIn: 'root' })
export class RedeemWindow {
  private state$ = new BehaviorSubject<RedeemState | null>(null);

  /** resolves after the first loadFromStorage() completes */
  readonly ready: Promise<void>;

  /** stream ของเวลาคงเหลือ (ms) อัปเดต ~ทุก 250ms และทันทีเมื่อ focus/visibility/storage เปลี่ยน */
  private countdownInternal$: Observable<number>;

  private vault = inject(Vault);

  constructor() {
    // โหลดสถานะจาก storage ครั้งแรก
    this.ready = this.loadFromStorage();

    // sync ระหว่างแท็บ: เมื่อ STORAGE_KEY เปลี่ยน ให้รีโหลด
    fromEvent<StorageEvent>(window, 'storage')
      .pipe(filter((e) => e.key === STORAGE_KEY))
      .subscribe(() => this.loadFromStorage());

    // แหล่งสัญญาณเวลา & เหตุการณ์เพื่ออัปเดตทันที
    const ticks$ = interval(250).pipe(startWith(0));
    const vis$ = fromEvent(document, 'visibilitychange').pipe(startWith(null));
    const focus$ = fromEvent(window, 'focus').pipe(startWith(null));

    this.countdownInternal$ = combineLatest([this.state$, ticks$, vis$, focus$]).pipe(
      map(([state]) => {
        if (!state) return 0;

        const now = Date.now();
        let remaining = state.expiresAt - now;

        // ปรับด้วย performance.now() กันเคสผู้ใช้เลื่นเวลาถอยหลังแบบหยาบ ๆ
        const perfElapsed = performance.now() - state.startedAtPerf;
        // now ที่ "ขั้นต่ำ" ที่ควรเป็น = issuedAt + เวลาที่ผ่านไปตาม perf - buffer
        const expectedMinNow = state.issuedAt + perfElapsed - 1500; // buffer 1.5s
        if (now < expectedMinNow) {
          // ถ้านาฬิกาถอยหลัง ให้ยึดตาม perfElapsed
          remaining = Math.max(0, state.expiresAt - (state.issuedAt + perfElapsed));
        }

        return Math.max(0, Math.floor(remaining));
      }),
      distinctUntilChanged(),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
  }

  /** เริ่มหน้าต่างเวลา (ดีฟอลต์ 10 นาที) */
  async startWindow(
    codeId: string,
    merchantName: string,
    durationMs = DEFAULT_WINDOW_MS,
  ): Promise<void> {
    const issuedAt = Date.now();

    const newState: RedeemState = {
      codeId,
      merchantName,

      issuedAt,
      expiresAt: issuedAt + durationMs,
      startedAtPerf: performance.now(),
    };
    this.state$.next(newState);
    await this.vault.set(STORAGE_KEY, newState);
  }

  /** เคลียร์ state (เมื่อหมดอายุ/ยกเลิก/ใช้เสร็จ) */
  clear(): void {
    this.vault.remove(STORAGE_KEY);
    this.state$.next(null);
  }

  /** เวลาคงเหลือ (ms). จะเป็น 0 เมื่อหมดเวลา หรือยังไม่มีหน้าต่าง */
  countdown$(): Observable<number> {
    return this.countdownInternal$;
  }

  /** รหัสปัจจุบัน (ถ้ามี) */
  codeId$(): Observable<string | null> {
    return this.state$.pipe(
      map((s) => s?.codeId ?? null),
      distinctUntilChanged(),
      shareReplay(1),
    );
  }

  /** state ล่าสุดแบบ synchronous */
  getSnapshot(): RedeemState | null {
    return this.state$.value;
  }

  /** merchant ที่ผูกกับหน้าต่างที่ active อยู่ (ถ้ามี) */
  getMerchantName(): string | null {
    return this.getSnapshot()?.merchantName ?? null;
  }

  /** true ถ้าหมดอายุแล้วหรือไม่มีหน้าต่าง */
  isExpired(): boolean {
    const s = this.state$.value;

    return !s || Date.now() >= s.expiresAt;
  }

  hasActiveWindow(): boolean {
    const s = this.getSnapshot();
    return !!s && Date.now() < s.expiresAt;
  }

  /** โหลดจาก storage (เรียกอัตโนมัติแล้วใน constructor; เรียกซ้ำได้) */
  async loadFromStorage(): Promise<void> {
    const s = await this.vault.get<RedeemState>(STORAGE_KEY);

    if (!s) {
      this.state$.next(null);
      return;
    }
    // ถ้า loaded แล้วหมดอายุ → เคลียร์ทิ้ง
    if (Date.now() >= s.expiresAt) {
      this.clear();
      return;
    }

    // เสียบค่า startedAtPerf ใหม่สำหรับรอบ session นี้
    this.state$.next({ ...s, startedAtPerf: performance.now() });
  }
}
